//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useCallback } from 'react';

import { type ApprovalDecision, type PageSnapshot } from '@/agent/contract';
import { agentService } from '@/services';
import { useApiErrorHandler } from './useApiErrorHandler';

/**
 * The agent runtime's calls behind the shared 401 policy, as useLLMActions
 * wraps the one-shot LLM calls.  Subscribes to no React context.
 */
export function useAgentActions() {
  const handleError = useApiErrorHandler();

  const createConversation = useCallback(() =>
    handleError('createConversation',
                () => agentService.createConversation()),
  [handleError]);

  const getConversation = useCallback((id: string) =>
    handleError('getConversation', () => agentService.getConversation(id)),
  [handleError]);

  const sendTurn = useCallback((
    id: string, message: string, snapshot: PageSnapshot, llm: string
  ) => handleError('sendTurn',
                   () => agentService.sendTurn(id, message, snapshot, llm)),
  [handleError]);

  const sendApproval = useCallback((
    id: string, approvalId: string, decision: ApprovalDecision
  ) => handleError('sendApproval',
                   () => agentService.sendApproval(id, approvalId, decision)),
  [handleError]);

  return { createConversation, getConversation, sendTurn, sendApproval };
}
